import type { Node } from '@xyflow/react'

interface CollisionOptions {
  maxIterations?: number
  overlapThreshold?: number
  margin?: number
  /**
   * Nodes the user just placed (e.g. the nodes of a finished drag). They stay
   * exactly where they were dropped and only push the nodes they overlap;
   * overlaps elsewhere on the canvas are left alone.
   */
  fixedIds?: ReadonlySet<string>
}

interface Box {
  x: number
  y: number
  width: number
  height: number
  moved: boolean
  fixed: boolean
  node: Node
}

function getBoxesFromNodes(nodes: Node[], margin: number, fixedIds?: ReadonlySet<string>): Box[] {
  return nodes.map(node => ({
    x: node.position.x - margin,
    y: node.position.y - margin,
    width: (node.measured?.width ?? node.width ?? 0) + margin * 2,
    height: (node.measured?.height ?? node.height ?? 0) + margin * 2,
    node,
    moved: false,
    fixed: fixedIds?.has(node.id) ?? false,
  }))
}

export function resolveCollisions(
  nodes: Node[],
  { maxIterations = 50, overlapThreshold = 0.5, margin = 15, fixedIds }: CollisionOptions = {},
): Node[] {
  const boxes = getBoxesFromNodes(nodes, margin, fixedIds)

  for (let iter = 0; iter <= maxIterations; iter++) {
    let moved = false

    for (let i = 0; i < boxes.length; i++) {
      for (let j = i + 1; j < boxes.length; j++) {
        const A = boxes[i]
        const B = boxes[j]

        // With placed nodes, only resolve overlaps they caused (directly or by a push chain).
        if (fixedIds && !(A.fixed || A.moved || B.fixed || B.moved)) continue
        if (A.fixed && B.fixed) continue

        const dx = (A.x + A.width * 0.5) - (B.x + B.width * 0.5)
        const dy = (A.y + A.height * 0.5) - (B.y + B.height * 0.5)

        const px = (A.width + B.width) * 0.5 - Math.abs(dx)
        const py = (A.height + B.height) * 0.5 - Math.abs(dy)

        if (px > overlapThreshold && py > overlapThreshold) {
          moved = true
          // A placed node never yields: the other node takes the whole push.
          const shareA = A.fixed ? 0 : B.fixed ? 1 : 0.5
          const shareB = 1 - shareA
          if (shareA) A.moved = true
          if (shareB) B.moved = true
          if (px < py) {
            const direction = dx > 0 ? 1 : -1
            A.x += px * shareA * direction
            B.x -= px * shareB * direction
          } else {
            const direction = dy > 0 ? 1 : -1
            A.y += py * shareA * direction
            B.y -= py * shareB * direction
          }
        }
      }
    }

    if (!moved) break
  }

  return boxes.map(box =>
    box.moved
      ? { ...box.node, position: { x: box.x + margin, y: box.y + margin } }
      : box.node
  )
}
